import React, { useState, Fragment } from 'react';
import styled from '@emotion/styled';
import TabButton from './tab-button';
import { Grid, Typography } from '@mui/material';
import { Experiences } from '../constants';
import ExperienceCard from './experience-card';
import { BoxIcon, GroupIcon, OneIcon, VideosIcon } from './icons';
import { Link } from '@/components/link/link';
import { ISanityExperience } from '../types';

const ExperienceButtons = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  height: 65px;
  width: auto;
  background: ${({ theme }) => theme.palette.common.cream};
  border-radius: 100px;
  padding: 13px 34px;
  margin: 150px 0 0 0;
`;
const Star = styled.img`
  height: 20px;
  width: 19px;
  margin: 5px;
`;
const DetailedExperienceTitle = styled(Typography)`
  font-family: var(--font-work-sans), sans-serif;
  font-weight: 500;
  font-size: 28px;
  line-height: 127.3%;
  color: #000000;
  margin-bottom: 20px;
`;
const ExperiencesText = styled(Typography)`
  font-family: var(--font-work-sans), sans-serif;
  line-height: 159.3%;
  z-index: 1;
  font-size: 16px;
  width: 100%;
  color: #000000;
`;

const LinkAllExperience = styled(Typography)`
  font-family: var(--font-cooper-light), sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 136.8%;
  color: #4d66b1;
  margin-bottom: 15px;
`;
const ExperiencesDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  padding: 0 12.5%;
  height: auto;
  & a {
    :hover {
      text-decoration: none;
    }
  }
`;
const ExperienceDiv = styled(Grid)`
  display: flex;
  margin-top: 102px;
  flex-direction: column;
  justify-content: flex-start;
  padding-left: 12px;
`;
const ExperienceVideoDiv = styled(Grid)`
  display: flex;
  margin-top: 102px;
  flex-direction: column;
  justify-content: flex-start;
  padding: 0;
`;

const ExperienceTab = styled(Grid)`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  height: 420px;
`;
const ExperienceImage = styled.img`
  width: 100%;
  height: auto;
`;
const Video = styled.div`
  height: 412px;
  width: 73.333%;
  z-index: 2;
  margin-left: 35px;
  border-radius: 28px;
  @media (max-width: 768px) {
    width: 100%;
    height: auto;
  }
`;

const ExperienceCardsDiv = styled(Grid)`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin-top: 90px;
`;
const Card = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  z-index: 99;
`;
const Dots = styled.img`
  margin-top: 62px;
  height: 5px;
  width: 35px;
`;
interface DeskTopViewProps {
  experiences: ISanityExperience[] | undefined;
  archiveMode?: boolean;
}

const DeskTopView: React.FC<DeskTopViewProps> = ({ experiences, archiveMode = false }) => {
  const [index, setIndex] = useState<number>(0);
  const selectedExperience = experiences?.[index];
  const archiveGroupExperienceText =
    'Curiko group experiences were great way to make connections and learn from one another. People encountered different ideas and exchanged thoughts. Group experiences happened online and in person.';
  const archiveOneOnOneExperienceText =
    'Curiko one-on-one experiences were an opportunity to meet with a Host to explore a passion or pursuit. Participants asked questions, swapped stories, and set the focus together. They took place both online and in person.';
  const detailText = archiveMode
    ? selectedExperience?.experienceType?.toLowerCase() === 'group'
      ? archiveGroupExperienceText
      : selectedExperience?.experienceType?.toLowerCase() === 'one-on-one'
        ? archiveOneOnOneExperienceText
        : selectedExperience?.detail
    : selectedExperience?.detail;

  return (
    <div>
      <ExperiencesDiv>
        <ExperienceButtons>
          {experiences?.map((experience, idx) => {
            const Icon =
              experience.experienceType === 'One-on-one'
                ? OneIcon
                : experience.experienceType === 'Group'
                  ? GroupIcon
                  : experience.experienceType === 'Box'
                    ? BoxIcon
                    : VideosIcon;
            return (
              <Fragment key={idx}>
                <TabButton
                  startIcon={<Icon color={index === idx ? '#FFF3DC' : '#251628'} />}
                  className={index === idx ? 'active' : ''}
                  onClick={() => setIndex(idx)}
                >
                  {experience.experienceType}
                </TabButton>
                <Star src="/discover/experiences/star.png" alt={'star'} />
              </Fragment>
            );
          })}
        </ExperienceButtons>
        <ExperienceTab container>
          <ExperienceVideoDiv item md={6} xs={12}>
            <Video>
              <ExperienceImage src={experiences?.[index].video} />
            </Video>
          </ExperienceVideoDiv>
          <ExperienceDiv item md={6} xs={12}>
            <DetailedExperienceTitle>{selectedExperience?.title}</DetailedExperienceTitle>
            <Link href={Experiences[index].link || ''}>
              <LinkAllExperience>{selectedExperience?.name}</LinkAllExperience>
            </Link>
            <ExperiencesText>{detailText}</ExperiencesText>
          </ExperienceDiv>
        </ExperienceTab>
      </ExperiencesDiv>
      <ExperienceCardsDiv>
        {experiences?.[index]?.steps?.map((m, _index: number) => (
          <Card key={_index}>
            <ExperienceCard image={m.icon!} name={m.name!} />
            {_index != experiences?.[index].steps!.length - 1 ? (
              <Dots src="/discover/experiences/dots.png" alt="" />
            ) : null}
          </Card>
        ))}
      </ExperienceCardsDiv>
    </div>
  );
};

export default DeskTopView;
